/**
 * Handle resolution helpers for DXF cross-references.
 *
 * Extracted verbatim from parser.ts (no semantic change).
 *
 * MLEADERSTYLE entries in the OBJECTS section reference text styles,
 * line types, and blocks via group codes 340/341/342/343 — these are
 * captured as opaque handle strings during the first parse pass because
 * the target entries may live in sections that haven't been parsed yet.
 * The two-pass resolver below builds handle → name maps from each
 * source section and rehydrates the human-readable names on the
 * MLEADERSTYLE records.
 *
 * NOTE: per CLAUDE.md "split file imports" rule — when other modules
 * inside `packages/dxf-io` need these helpers they MUST import them
 * from `./handleResolution` directly. parser.ts now re-exports them
 * for external consumers but local callers still need the explicit
 * import.
 */

import type {
  DxfBlocksSection,
  DxfEntitiesSection,
  DxfGroup,
  DxfMultileaderStyleEntry,
  DxfObjectsSection,
  DxfSection,
  DxfSupportedEntity,
} from "./types";
import type {
  AcDbScale,
  AnnotScaleContext,
} from "./objects/objectsAnnotativeParser";
import type { DxfSpatialFilter } from "./objects/spatialFilterTypes";
import type {
  AssocActionValueParam,
  AssocArrayItem,
  AssocArrayRectParams,
  AssocVertexActionParam,
  DxfAssocArrayObject,
} from "./assocArrayParser";
import { collectAssocVertexActionParams } from "./assocArrayParser";
import { normalizeHandle } from "./handleNormalize";

/**
 * Re-key a raw-handle-keyed handle→name map by {@link normalizeHandle} so a
 * case-mismatched hex ref still resolves (DXF handles are hex + CASE-INSENSITIVE:
 * `1c` and `1C` name the same record). The STYLE table map is already
 * normalize-keyed at build time (`buildTextStyleHandleMap`); the LTYPE / BLOCK
 * maps (`buildLineTypeHandleMap` / `buildBlockHandleMap`) key by the verbatim
 * group-5 handle, so any resolver that looks THOSE up by a possibly-differently-
 * cased ref must route through this. Single source of truth shared by
 * `resolveMultileaderStyleHandles` and `resolveMLeaderBlockHandles`.
 */
function normalizeHandleMap(
  raw: ReadonlyMap<string, string>,
): Map<string, string> {
  const normalized = new Map<string, string>();
  for (const [handle, name] of raw) {
    normalized.set(normalizeHandle(handle), name);
  }
  return normalized;
}

export function resolveMultileaderStyleHandles(
  mleaderStyles: DxfMultileaderStyleEntry[],
  textStyleHandleToName: Map<string, string>,
  blockHandleToName: Map<string, string>,
  lineTypeHandleToName: Map<string, string>,
): void {
  // DXF handles are hex + CASE-INSENSITIVE. The STYLE map is already keyed by
  // normalizeHandle (buildTextStyleHandleMap), so its lookup normalizes the ref.
  // The LTYPE / BLOCK maps are keyed by the RAW group-5 handle
  // (buildLineTypeHandleMap / buildBlockHandleMap), so a MLEADERSTYLE ref that
  // differs only in case (e.g. `340 1c` vs the LTYPE record's `5 1C`) MISSED and
  // the style fell back to defaults on save/reopen. Normalize those two maps into
  // case-insensitive lookup copies AND normalize every ref — mirroring
  // resolveMLeaderBlockHandles / the annotative/assoc resolvers.
  const lineTypeNormalized = normalizeHandleMap(lineTypeHandleToName);
  const blockNormalized = normalizeHandleMap(blockHandleToName);
  for (const style of mleaderStyles) {
    if (style.textStyleHandle) {
      // 342 text_style_handle → STYLE. Map already normalize-keyed; normalize the
      // ref the same way (Codex #243 F2).
      const name = textStyleHandleToName.get(normalizeHandle(style.textStyleHandle));
      if (name) style.textStyleName = name;
    }
    if (style.leaderLineTypeHandle) {
      // 340 leader_linetype_handle → LTYPE.
      const name = lineTypeNormalized.get(normalizeHandle(style.leaderLineTypeHandle));
      if (name) style.leaderLineType = name;
    }
    if (style.arrowheadBlockHandle) {
      // 341 arrow_head_handle → BLOCK_RECORD.
      const name = blockNormalized.get(normalizeHandle(style.arrowheadBlockHandle));
      if (name) style.arrowheadBlock = name;
    }
    if (style.blockContentHandle) {
      // 343 block_record_handle → BLOCK_RECORD.
      const name = blockNormalized.get(normalizeHandle(style.blockContentHandle));
      if (name) style.blockContentName = name;
    }
  }
}

/**
 * Second-pass resolver for the MLEADER **entity** text-style handle
 * (group 340 inside the CONTEXT_DATA / MTextData block — ezdxf
 * `MTextData.style_handle`, "handle of TextStyle() table entry").
 *
 * The entity parser captures the raw handle on
 * `geometry.textStyleHandle` because the STYLE table may not be parsed
 * yet. Here — once every section is available — we swap it for the
 * human-readable STYLE name on `geometry.textStyleName`, mirroring how
 * `resolveMultileaderStyleHandles` rehydrates MLEADERSTYLE references.
 * The raw handle is kept so the writer can re-emit group 340 verbatim
 * (no fabricated handle).
 */
export function resolveMLeaderTextStyleHandles(
  entities: DxfSupportedEntity[],
  textStyleHandleToName: Map<string, string>,
): void {
  for (const entity of entities) {
    if (entity.type !== "MLEADER") continue;
    const handle = entity.geometry.textStyleHandle;
    if (!handle) continue;
    // Case-insensitive lookup: `a7` (340 ref) must resolve a STYLE record
    // indexed as `A7` (group 5). The map is keyed by `normalizeHandle`,
    // so normalize the lookup the same way (Codex #243 F2).
    const name = textStyleHandleToName.get(normalizeHandle(handle));
    if (name) entity.geometry.textStyleName = name;
  }
}

/**
 * Second-pass resolver for the MLEADER **entity** leader-style handle
 * (AcDbMLeader BODY group 340 = `style_handle`, the MLEADERSTYLE record handle,
 * emitted right AFTER CONTEXT_DATA — ezdxf `acdb_mleader` `style_handle`).
 *
 * The entity parser captures the raw depth-0 body 340 on
 * `geometry.leaderStyleHandle` (the OBJECTS section may not be parsed yet). Here
 * — once every section is available — we resolve it to the human-readable
 * MLEADERSTYLE NAME on `geometry.styleName`, mirroring
 * `resolveMLeaderTextStyleHandles` for the text style. Without this an imported
 * MLEADER referencing a NON-Standard leader style read back as "Standard" (the
 * name never left the OBJECTS graph), so the chosen style was invisible to the
 * model / UI and lost on the next save. The raw handle stays on
 * `leaderStyleHandle` so a byte-faithful re-export re-emits 340 verbatim; the
 * document orchestrator only re-mints it when the style is re-emitted / edited.
 * Handle lookup is case-insensitive (hex handles, #243 F2).
 */
export function resolveMLeaderLeaderStyleHandles(
  entities: DxfSupportedEntity[],
  mleaderStyleHandleToName: Map<string, string>,
): void {
  if (mleaderStyleHandleToName.size === 0) return;
  for (const entity of entities) {
    if (entity.type !== "MLEADER") continue;
    const handle = entity.geometry.leaderStyleHandle;
    if (!handle) continue;
    const name = mleaderStyleHandleToName.get(normalizeHandle(handle));
    if (name) entity.geometry.styleName = name;
  }
}

/**
 * Second-pass resolver for the MLEADER **entity** block-content handle
 * (AcDbMLeader BODY group 344 = block_record_handle, ezdxf Scope-6).
 *
 * The entity parser captures the raw 344 handle on
 * `geometry.blockRecordHandle` (the BLOCK_RECORD table may not be parsed
 * yet). Here we swap in the human-readable block NAME on
 * `geometry.blockName` — mirroring `resolveMLeaderTextStyleHandles` for the
 * text style. The raw handle stays on `blockRecordHandle` so the writer can
 * re-emit group 344 verbatim (byte-faithful; no fabricated handle). Without
 * this, `blockName` kept the raw handle (e.g. "8A") and block lookup /
 * rendering pointed at a non-existent block whenever handle != name
 * (Codex #271). Handle lookup is case-insensitive (hex handles, #243 F2).
 */
export function resolveMLeaderBlockHandles(
  entities: DxfSupportedEntity[],
  blockHandleToName: Map<string, string>,
): void {
  // Normalize the (raw-keyed) block map once so a case-mismatched 344 ref still
  // resolves (the BLOCK_RECORD scan keys by raw handle; the 344 ref may differ
  // in case). Shared one-truth normalization with resolveMultileaderStyleHandles.
  const normalized = normalizeHandleMap(blockHandleToName);
  for (const entity of entities) {
    if (entity.type !== "MLEADER") continue;
    const handle = entity.geometry.blockRecordHandle;
    if (!handle) continue;
    const name = normalized.get(normalizeHandle(handle));
    if (name) entity.geometry.blockName = name;
  }
}

/**
 * Pass-2 handle-resolution orchestrator shared by the eager (`parser.ts`)
 * and streaming (`streamingParser.ts`) parsers.
 *
 * MLEADERSTYLE entries (OBJECTS) and MLEADER entities reference STYLE /
 * LTYPE / BLOCK records by handle (group 340/341/342/343 for the style,
 * group 340 inside CONTEXT_DATA for the entity). The handle maps can only
 * be built once every section is visited, so both parsers call this AFTER
 * their section-collection loop. Building the STYLE handle map (and the
 * LTYPE / BLOCK maps) is skipped entirely when there is nothing to
 * resolve. Mutates the parsed records in place.
 */
export function resolveDxfMLeaderHandles(sections: DxfSection[]): void {
  const objectsSection = sections.find(
    (s): s is DxfObjectsSection => s.kind === "objects-section",
  );
  const entitiesSection = sections.find(
    (s): s is DxfEntitiesSection => s.kind === "entities-section",
  );
  const hasMleaderStyles =
    objectsSection !== undefined &&
    objectsSection.multileaderStyles.length > 0;
  const topLevelEntities =
    entitiesSection?.entities.filter(
      (e): e is DxfSupportedEntity => e.kind === "supported-entity",
    ) ?? [];

  // Codex #243 F3: MLEADERs also appear INSIDE BLOCKS-section block
  // definitions (parseBlockBody parses them as supported MLEADER). Those
  // entities carry only `geometry.textStyleHandle` from the parse and
  // never get `textStyleName` unless we resolve them here against the
  // same STYLE table. (The dxf-bridge may later drop block MLEADERs until
  // S-BLOCKCHILD-MLEADER lands, but the dxf-io resolver must still
  // resolve them correctly so the parsed model is faithful.)
  const blockSections = sections.filter(
    (s): s is DxfBlocksSection => s.kind === "blocks-section",
  );
  const blockEntities: DxfSupportedEntity[] = [];
  for (const blocks of blockSections) {
    for (const block of blocks.blocks) {
      for (const e of block.entities) {
        if (e.kind === "supported-entity") blockEntities.push(e);
      }
    }
  }

  // Resolve top-level and block-section MLEADERs together against one map.
  const supportedEntities = [...topLevelEntities, ...blockEntities];
  const hasMleaderEntities = supportedEntities.some(
    (e) => e.type === "MLEADER",
  );
  if (!hasMleaderStyles && !hasMleaderEntities) return;

  const textStyleMap = buildTextStyleHandleMap(sections);
  // Build the BLOCK_RECORD handle→name map once — needed by MLEADERSTYLE
  // resolution AND by MLEADER entity block-content (body 344) resolution.
  const blockMap = buildBlockHandleMap(sections);
  if (hasMleaderStyles) {
    const lineTypeMap = buildLineTypeHandleMap(sections);
    resolveMultileaderStyleHandles(
      objectsSection.multileaderStyles,
      textStyleMap,
      blockMap,
      lineTypeMap,
    );
  }
  if (hasMleaderEntities) {
    resolveMLeaderTextStyleHandles(supportedEntities, textStyleMap);
    // Resolve the MLEADER entity body-344 BLOCK_RECORD handle → block name so
    // block content lookup/rendering targets a real block (Codex #271).
    resolveMLeaderBlockHandles(supportedEntities, blockMap);
    // SL-5 P2 — resolve the MLEADER entity body-340 MLEADERSTYLE handle → the
    // leader-style NAME so an imported non-Standard leader style survives on the
    // model (and is not lost as "Standard" on the next save). Keyed by
    // normalizeHandle like the STYLE table map. No-op when the file has no
    // MLEADERSTYLE objects (map empty).
    if (hasMleaderStyles && objectsSection) {
      const mleaderStyleMap = new Map<string, string>();
      for (const style of objectsSection.multileaderStyles) {
        if (style.handle && style.name) {
          mleaderStyleMap.set(normalizeHandle(style.handle), style.name);
        }
      }
      resolveMLeaderLeaderStyleHandles(supportedEntities, mleaderStyleMap);
    }
  }
}

// Build a linetype handle → name map so we can resolve linetype handle
// references (MLEADERSTYLE group code 341, DIMSTYLE group code 340, …).
//
// PR-LT2 promoted LTYPE records out of `rawTableGroups` into the TYPED
// `DxfTablesSection.linetypes[]` array. The PRIMARY source is therefore the
// typed array — each `DxfLinetypeEntry` carries `handle` (group 5) and `name`
// (group 2). We still fall back to scanning `rawTableGroups` for the LTYPE
// table so older callers / sections that never populated `linetypes[]`
// (legacy raw passthrough) keep resolving. Without the typed-array source the
// map would be EMPTY after PR-LT2 (rawTableGroups no longer holds LTYPE) and
// MLEADERSTYLE.leaderLineType would silently fail to resolve — a regression.
export function buildLineTypeHandleMap(
  sections: ReadonlyArray<DxfSection>,
): Map<string, string> {
  const map = new Map<string, string>();

  // PRIMARY: typed linetypes[] (PR-LT2). Populated for every modern parse.
  for (const section of sections) {
    if (section.kind !== "tables-section") continue;
    for (const lt of section.linetypes ?? []) {
      if (lt.handle && lt.name) map.set(lt.handle, lt.name);
    }
  }

  // FALLBACK: scan rawTableGroups for an LTYPE table (legacy passthrough or
  // any section that never populated linetypes[]). Does not overwrite an
  // entry already resolved from the typed array.
  for (const section of sections) {
    if (section.kind !== "tables-section") continue;
    scanRawLineTypeGroups(section.rawTableGroups, map);
  }

  return map;
}

// Scan raw TABLES groups for LTYPE entries, adding handle → name pairs to
// `map` WITHOUT overwriting existing (typed-array-sourced) entries.
function scanRawLineTypeGroups(
  rawTableGroups: DxfGroup[],
  map: Map<string, string>,
): void {
  let currentTable = "";
  let currentName = "";
  let currentHandle = "";
  let inEntry = false;

  const flush = () => {
    if (inEntry && currentName && currentHandle && !map.has(currentHandle)) {
      map.set(currentHandle, currentName);
    }
  };

  for (const g of rawTableGroups) {
    if (g.code === 0 && g.value === "TABLE") {
      currentTable = "";
      inEntry = false;
      continue;
    }
    if (g.code === 2 && !inEntry) {
      currentTable = g.value.trim();
      continue;
    }
    if (g.code === 0 && g.value === "LTYPE" && currentTable === "LTYPE") {
      flush();
      inEntry = true;
      currentName = "";
      currentHandle = "";
      continue;
    }
    if (g.code === 0 && g.value === "ENDTAB") {
      flush();
      inEntry = false;
      currentTable = "";
      continue;
    }
    if (inEntry) {
      if (g.code === 2) currentName = g.value.trim();
      else if (g.code === 5) currentHandle = g.value.trim();
    }
  }
  flush();
}

// Scan a DXF raw BLOCKS section for block definitions and build a
// handle → name map. Each BLOCK record exposes its name via group 2
// and its handle via group 5 (BLOCK_RECORD references use the
// BLOCK_RECORD handle, but for the Standard round-trip we pair via the
// block's own handle which AutoCAD treats as equivalent at parse time).
export function buildBlockHandleMap(sections: DxfSection[]): Map<string, string> {
  const map = new Map<string, string>();

  // PRIMARY: typed BLOCK_RECORD entries. The modern parser promotes the
  // BLOCK_RECORD table into the typed `DxfTablesSection.blockRecords[]` array
  // (mirroring textStyles/linetypes) and leaves `rawTableGroups` empty, so the
  // raw scans below would miss it entirely — MLEADER body-344 / MLEADERSTYLE
  // block handles then never resolve (Codex #271).
  for (const section of sections) {
    if (section.kind !== "tables-section") continue;
    for (const br of section.blockRecords ?? []) {
      if (br.handle && br.name && !map.has(br.handle)) {
        map.set(br.handle, br.name);
      }
    }
  }

  // Next try TABLES → BLOCK_RECORD entries via the raw group scan (legacy
  // passthrough / sections that never populated blockRecords[]).
  for (const section of sections) {
    if (section.kind !== "tables-section") continue;
    let inBlockRecordTable = false;
    let currentName = "";
    let currentHandle = "";
    let inEntry = false;

    for (const g of section.rawTableGroups) {
      if (g.code === 0 && g.value === "TABLE") {
        inBlockRecordTable = false;
        inEntry = false;
        continue;
      }
      if (g.code === 2 && !inEntry) {
        inBlockRecordTable = g.value.trim() === "BLOCK_RECORD";
        continue;
      }
      if (!inBlockRecordTable) continue;
      if (g.code === 0 && g.value === "BLOCK_RECORD") {
        if (inEntry && currentName && currentHandle) {
          map.set(currentHandle, currentName);
        }
        inEntry = true;
        currentName = "";
        currentHandle = "";
        continue;
      }
      if (g.code === 0 && g.value === "ENDTAB") {
        if (inEntry && currentName && currentHandle) {
          map.set(currentHandle, currentName);
        }
        inEntry = false;
        inBlockRecordTable = false;
        continue;
      }
      if (inEntry) {
        if (g.code === 2) currentName = g.value.trim();
        else if (g.code === 5) currentHandle = g.value.trim();
      }
    }
  }

  // Also scan structured BLOCKS section if present. AutoCAD stores each
  // BLOCK definition with its own handle (5) and name (2), which can be
  // used as a fallback when BLOCK_RECORD entries are missing.
  for (const section of sections) {
    if (section.kind === "blocks-section") {
      for (const block of section.blocks) {
        if (block.name && block.handle && !map.has(block.handle)) {
          map.set(block.handle, block.name);
        }
      }
      continue;
    }
    // Legacy path: some callers still pass raw-section BLOCKS through
    // this helper (dimension stub builder output, parser fallback). We
    // keep a manual scan so handle resolution keeps working.
    if (section.kind !== "raw-section" || section.name !== "BLOCKS") continue;
    let inBlock = false;
    let currentName = "";
    let currentHandle = "";
    for (const g of section.groups) {
      if (g.code === 0 && g.value === "BLOCK") {
        inBlock = true;
        currentName = "";
        currentHandle = "";
        continue;
      }
      if (g.code === 0 && g.value === "ENDBLK") {
        if (currentName && currentHandle && !map.has(currentHandle)) {
          map.set(currentHandle, currentName);
        }
        inBlock = false;
        continue;
      }
      if (inBlock) {
        if (g.code === 2) currentName = g.value.trim();
        else if (g.code === 5 && !currentHandle) currentHandle = g.value.trim();
      }
    }
  }

  return map;
}

// `normalizeHandle` moved to ./handleNormalize (Codex #251-A follow-up) so the
// OBJECTS pre-scan paths (sectionParsers / assocArrayParser) can share the SAME
// normalization without importing this module (which would cycle through the
// assocArrayParser type imports above). Re-exported here so every existing
// consumer of `handleResolution.normalizeHandle` keeps working unchanged; the
// local `import` below is REQUIRED in addition to the re-export — a re-export
// does NOT bring the symbol into this module's own scope (dep-graph-refactor
// rule 3).
export { normalizeHandle } from "./handleNormalize";

export function buildTextStyleHandleMap(sections: DxfSection[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const section of sections) {
    if (section.kind !== "tables-section") continue;
    for (const style of section.textStyles) {
      if (style.handle) {
        // Normalize the key so case-mismatched 340 refs still resolve.
        map.set(normalizeHandle(style.handle), style.name);
      }
    }
  }
  return map;
}

// ── Shared OBJECTS-resolver helpers ───────────────────────────────────

/**
 * A DICTIONARY node indexed by handle for the second-pass resolvers. Carries
 * the dict's owner-handle (to walk the owner chain back to an entity dict) and
 * its name→handle entries.
 */
type DictNode = {
  ownerHandle?: string;
  entries: ReadonlyArray<{ name: string; handle: string }>;
};

/**
 * Build a `Map<dictHandle, DictNode>` from an OBJECTS section's dictionaries.
 * Shared one-truth index used by every second-pass resolver that walks the
 * DICTIONARY owner chain (annotative contexts, associative arrays, …) so the
 * indexing logic exists in exactly one place.
 */
function buildDictionaryIndex(
  objectsSection: DxfObjectsSection,
): Map<string, DictNode> {
  const dictByHandle = new Map<string, DictNode>();
  for (const dict of objectsSection.dictionaries) {
    if (!dict.handle) continue;
    const node: DictNode = { entries: dict.entries };
    if (dict.ownerHandle !== undefined) node.ownerHandle = dict.ownerHandle;
    dictByHandle.set(dict.handle, node);
  }
  return dictByHandle;
}

// ── Annotative context resolution (PR-2) ──────────────────────────────

/**
 * Result of `resolveAnnotativeContexts` per entity. Surfaces the typed
 * contexts list plus a count of orphan references encountered (for
 * audit-log telemetry on the bridge side).
 */
export type AnnotativeContextResolution = {
  /** Map of entity handle → resolved per-scale context list. */
  entityContexts: Map<string, AnnotScaleContext[]>;
  /**
   * Count of context-data records that referenced a missing AcDbScale
   * handle. Surfaces to the audit log so users see warnings when
   * annotative data is partially corrupt.
   */
  orphanScaleReferences: number;
  /**
   * Count of context-data records whose ownerHandle did not chain back
   * to any entity-side extension dictionary. Round-trip preserved
   * through `objectsSection.annotScaleContexts` (writer re-emits them);
   * surfaced here for audit-log telemetry.
   */
  orphanOwnerReferences: number;
};

/**
 * Second-pass resolver for annotative context-data records.
 *
 * Inputs (all read-only):
 *   - `objectsSection.acadScales`       — handle → AcDbScale catalogue
 *   - `objectsSection.annotScaleContexts` — pending records to attach
 *   - `objectsSection.dictionaries`     — for owner-handle → entity chain
 *   - `entities`                        — ENTITIES (and BLOCK children)
 *                                         that may carry an
 *                                         extensionDictionaryHandle
 *
 * Output: `AnnotativeContextResolution` whose `entityContexts` map keys
 * each entity by its handle, listing every per-scale context that
 * resolved successfully. Orphan-counts are surfaced for audit logging
 * but never throw — fidelity is preserved through the verbatim
 * round-trip path on `objectsSection.annotScaleContexts[]`.
 *
 * Resolution chain (per AutoCAD ObjectARX reference):
 *
 *   entity (TEXT/MTEXT/INSERT/HATCH/...) — `extensionDictionaryHandle`
 *     ↓
 *   DICTIONARY — extensionDict (one per entity, owner = entity)
 *     ↓ (entry "ACAD_ANNOTATIVE_DATA" → handle)
 *   DICTIONARY — annotativeDataDict (owner = extensionDict)
 *     ↓ (entry "ACDB_CONTEXTDATA_TABLE" → handle)
 *   DICTIONARY — contextDataTable (owner = annotativeDataDict)
 *     ↓ (entries name=scaleName → context-data handle)
 *   AcDb*ObjectContextData — `annotScaleContexts` records
 *
 * In practice, AutoCAD often skips the intermediate dictionaries and
 * the context-data records' `ownerHandle` (group 330) points DIRECTLY
 * at the entity's extension dict. The resolver therefore tries the
 * direct anchor first and falls back to the full dict chain only for
 * cases where the ownerHandle does not land on any entity.
 *
 * Performance: O(N) — every collection is hashed once into Maps before
 * the resolution loop. На АБИ1.dxf (143 annotative entities, 111
 * context-data records, 19 scales) this completes in <2ms.
 */
export function resolveAnnotativeContexts(
  objectsSection: DxfObjectsSection,
  entities: ReadonlyArray<DxfSupportedEntity>,
): AnnotativeContextResolution {
  const result: AnnotativeContextResolution = {
    entityContexts: new Map(),
    orphanScaleReferences: 0,
    orphanOwnerReferences: 0,
  };

  const contexts = objectsSection.annotScaleContexts ?? [];
  if (contexts.length === 0) return result;

  // 1. Index AcDbScale by handle so context-data → scale lookup is O(1).
  const scaleByHandle = new Map<string, AcDbScale>();
  for (const s of objectsSection.acadScales ?? []) {
    if (s.handle) scaleByHandle.set(s.handle, s);
  }

  // 2. Index entities by their extensionDictionaryHandle so the direct
  // owner-handle path can resolve in O(1). Also keep the entity → handle
  // forward map so we can produce stable keys in the output.
  const entityByExtDictHandle = new Map<string, DxfSupportedEntity>();
  for (const entity of entities) {
    const extHandle = entity.common.extensionDictionaryHandle;
    if (extHandle) {
      entityByExtDictHandle.set(extHandle, entity);
    }
  }

  // 3. Index DICTIONARY entries — for the chain-walking fallback.
  // Each dict carries `entries[]` (name → handle pairs) and an optional
  // `ownerHandle` we use to follow the chain back to the entity dict.
  const dictByHandle = buildDictionaryIndex(objectsSection);

  // 4. Walk every collected context-data record. Resolve scale, find
  // the entity, push into the result map.
  for (const ctxRecord of contexts) {
    // a) Scale resolution.
    const scale = scaleByHandle.get(ctxRecord.scaleHandle);
    let scaleName = "";
    let scaleFactor = 1;
    if (!scale) {
      result.orphanScaleReferences += 1;
      // Continue with empty name so the entity still receives the
      // contextData (round-trip preserved through rawGroups). The bridge
      // layer will attach an audit warning surfacing this count.
    } else {
      scaleName = scale.name;
      scaleFactor = scale.scaleFactor;
    }

    // b) Entity anchor resolution. Try the direct ownerHandle first.
    let entity = entityByExtDictHandle.get(ctxRecord.ownerHandle);

    // Fallback: walk up the dictionary owner chain looking for a dict
    // whose handle is a known extensionDictionaryHandle. Bound the walk
    // to 8 hops to avoid degenerate cycles in malformed DXF.
    if (!entity && ctxRecord.ownerHandle) {
      let cursor: string | undefined = ctxRecord.ownerHandle;
      for (let hop = 0; hop < 8 && cursor; hop++) {
        const dict = dictByHandle.get(cursor);
        if (!dict) break;
        if (dict.ownerHandle && entityByExtDictHandle.has(dict.ownerHandle)) {
          entity = entityByExtDictHandle.get(dict.ownerHandle);
          break;
        }
        cursor = dict.ownerHandle;
      }
    }

    if (!entity || !entity.common.handle) {
      result.orphanOwnerReferences += 1;
      continue;
    }

    // c) Materialize the resolved context with scaleName/scaleFactor
    // patched in. We clone the discriminated-union member to avoid
    // mutating the round-trip copy on objectsSection.annotScaleContexts.
    const resolved: AnnotScaleContext = {
      ...ctxRecord.context,
      scaleName,
      scaleFactor,
    } as AnnotScaleContext;

    const key = entity.common.handle;
    const list = result.entityContexts.get(key);
    if (list) list.push(resolved);
    else result.entityContexts.set(key, [resolved]);
  }

  return result;
}

/**
 * Convenience accessor: map the resolution result onto a flat
 * (entityHandle, contexts[]) array for consumers (the bridge layer)
 * that prefer iteration over Map traversal.
 */
export function annotativeContextsToList(
  resolution: AnnotativeContextResolution,
): Array<{ entityHandle: string; contexts: AnnotScaleContext[] }> {
  const out: Array<{ entityHandle: string; contexts: AnnotScaleContext[] }> = [];
  for (const [entityHandle, contexts] of resolution.entityContexts) {
    out.push({ entityHandle, contexts });
  }
  return out;
}

// ── Associative array resolution (Batch-8 S-ARRAYASSOC-RESOLVE) ────────────

/**
 * Resolved associative-array data for a single ARRAY entity. Surfaces the
 * decoded rectangular parameters plus the model-side source entity ids (the
 * ARRAY's source entities, resolved from the AcDbAssocDependency FKs) and the
 * handle linkage so the bridge can populate the typed model + the writer can
 * keep the OBJECTS graph wired.
 */
export type ResolvedAssocArray = {
  /** Handle of the ARRAY entity this resolution belongs to. */
  entityHandle: string;
  /** Whether an AcDbAssocArray graph was found for this entity. */
  associative: boolean;
  /**
   * Decoded rectangular parameters. Two source shapes feed this (audit §12
   * S-ARRAYRECT-PARAMS-DECODE):
   *   1. a standalone ACDBASSOCARRAYRECTANGULARPARAMETERS record (synthetic /
   *      third-party shape) — positional decode, takes precedence per field;
   *   2. the NAMED value params on the owning ACDBASSOCACTION (the real
   *      AutoCAD shape: "Items"=columns, "Rows", "Levels", "ItemSpacing"=
   *      column spacing, "RowSpacing", "LevelSpacing", "RowElevation",
   *      "AxesAngle" [radians]) — fills fields the standalone record did not
   *      provide. Mapped ONLY when the graph is rectangular-family (the action
   *      body's inline params class name / a rect-params record) — polar
   *      "Items"/"ItemSpacing" mean count-around-circle / angle and are never
   *      projected onto rectangular fields.
   */
  rectParams?: AssocArrayRectParams;
  /**
   * The array's base-plane normal, decoded from the action-owned
   * ACDBASSOCVERTEXACTIONPARAM named "BaseNormal" (ObjectARX
   * `AcDbAssocArrayCommonParameters::setBasePlane`; verified against real
   * AutoCAD fixtures). Absent when the graph carries no such param —
   * never default-materialized.
   */
  basePlaneNormal?: { x: number; y: number; z: number };
  /**
   * AW-1 — handle of the owning ACDBASSOCACTION record (round-trip linkage
   * for the write-back encoder, `assocArrayPatch.ts`). The action, NOT the
   * action body, carries the named value params ("Items"/"Rows"/"AxesAngle"/
   * … — see `RECT_NAMED_VALUE_PARAMS`) the write-back surgically patches.
   * Distinct from `actionBodyHandle` (the ACDBASSOCARRAYACTIONBODY the
   * action points at via a 360). Absent when no reachable ACDBASSOCACTION
   * exists in the subgraph (e.g. a malformed / partial graph).
   */
  actionHandle?: string;
  /**
   * @codex P2 (local review, round-4) — true iff `rectParams.axisAngle` was
   * populated by the NAMED value-param path (`mergeNamedRectValueParams`),
   * which applies the AW-1 units seam (`axesAngleRadiansToModelDegrees`) and
   * is therefore GENUINELY degrees. `undefined`/`false` covers BOTH "no
   * axisAngle resolved at all" AND "resolved from the standalone
   * ACDBASSOCARRAYRECTANGULARPARAMETERS positional record" — that form is
   * NEVER unit-converted (its true wire convention has no fixture evidence,
   * see `axesAngleRadiansToModelDegrees`'s doc), so its raw value's unit
   * stays whatever it was BEFORE this resolution — preserving the historical
   * (round-1) radians assumption for that one unverified case, rather than
   * silently flipping it to degrees just because the named-form path
   * elsewhere in this same PR now converts. Consumers (`drawing-model`'s
   * `applyAssocArrayResolution`, `cad-core`'s `rotateByArrayAxisAngle`) use
   * this to decide the unit WITHOUT re-deriving "which form supplied it"
   * from scratch.
   */
  axisAngleFromNamedForm?: boolean;
  /**
   * @codex P2 ("Track the owning action per named field") — per-field
   * ACDBASSOCACTION owner handle, present ONLY when the array's named value
   * params are genuinely SPLIT across more than one action (see
   * `mergeNamedRectValueParams`'s doc for the full "why" and the collapse-to-
   * undefined rule for the common single-action case). The write-back
   * encoder (`assocArrayPatch.ts`) consults this BEFORE falling back to the
   * single `actionHandle` above, so an edit to a field owned by a SECOND
   * action still finds and patches the record that actually carries it.
   */
  ownerHandleByField?: AssocArrayFieldOwnerMap;
  /**
   * The inline per-item AcDbAssocArrayItem records decoded from the array's
   * ACDBASSOCARRAYACTIONBODY (audit §12 S-ARRAYRECT-ITEMS-DECODE) — grid
   * locator, flags (erased / modified), default transform (translation point
   * or full matrix), the per-item relative-transform OVERRIDE (moved/rotated
   * items — where per-item rotation lands), the entity-provider FK and the
   * modify-body FK. Surfaced ONLY for the RECTANGULAR family (the #303 gate
   * precedent: the item grammar is family-shared per ODA's base-class filer,
   * but the model home is `ArrayRectEntity.assocItems` — polar/path items
   * stay raw-only until their model entities exist). Absent when the body
   * persisted no items or the list failed the all-or-nothing decode.
   */
  items?: AssocArrayItem[];
  /** Source-entity handles (= model ids) resolved from AcDbAssocDependency. */
  sourceEntityIds: string[];
  /** Handle of the ACDBASSOCARRAYACTIONBODY record (round-trip linkage). */
  actionBodyHandle?: string;
  /** Handle of the rectangular-parameters record (round-trip linkage). */
  arrayParametersHandle?: string;
  /** Handles of the AcDbAssocDependency records (round-trip linkage). */
  dependencyHandles: string[];
};

/**
 * Result of `resolveAssocArrays`: a map of ARRAY entity handle → resolved
 * associative data, plus orphan telemetry for the audit log.
 */
export type AssocArrayResolution = {
  /** Map of ARRAY entity handle → resolved associative array. */
  byEntityHandle: Map<string, ResolvedAssocArray>;
  /**
   * Count of AcDbAssocDependency references that did not chain back to any
   * known entity handle (round-trip preserved through rawGroups; surfaced for
   * audit-log telemetry).
   */
  orphanDependencyReferences: number;
};

/**
 * Second-pass resolver for the AcDbAssocArray OBJECTS graph. Clones the shape
 * of `resolveAnnotativeContexts`: every collection is hashed once into Maps,
 * then a per-entity walk joins the entity to its decoded parameters + source
 * entities.
 *
 * Resolution chain (per the AcDbAssocArray ObjectARX object model):
 *
 *   ARRAY entity — `extensionDictionaryHandle`
 *     ↓ (the entity's extension dictionary)
 *   DICTIONARY — entries reference the ACDBASSOCNETWORK / action via 350/360
 *     ↓
 *   ACDBASSOCNETWORK → ACDBASSOCACTION → ACDBASSOCARRAYACTIONBODY
 *     ├─ ACDBASSOCARRAYRECTANGULARPARAMETERS   → decoded params
 *     └─ ACDBASSOCDEPENDENCY (one or more)     → source-entity handle FK
 *
 * Because the exact ownership wiring of the AcDbAssoc* objects is not part of
 * the public DXF spec, the resolver collects the array's subgraph robustly: it
 * seeds from the handles referenced by the entity's extension dictionary AND
 * any assoc object whose owner chains back to that dictionary, then walks the
 * owner graph (BFS) to gather every assoc object in the array. From that
 * subgraph it picks the first rectangular-parameters record + every dependency
 * reference, mapping each dependency FK to a known entity handle.
 *
 * Falls back gracefully: when an entity has no ext-dict OR no assoc graph is
 * reachable, it is simply absent from the result map (the bridge then leaves
 * the model's `associative`/`sourceEntityIds`/params at their non-associative
 * INSERT-grid defaults). Nothing throws; fidelity is preserved through the
 * verbatim `objectsSection.assocArrays[].rawGroups` round-trip.
 */
export function resolveAssocArrays(
  objectsSection: DxfObjectsSection,
  entities: ReadonlyArray<DxfSupportedEntity>,
): AssocArrayResolution {
  const result: AssocArrayResolution = {
    byEntityHandle: new Map(),
    orphanDependencyReferences: 0,
  };

  const assocArrays = objectsSection.assocArrays ?? [];
  if (assocArrays.length === 0) return result;

  // 1. Index assoc objects by handle + by ownerHandle (for the owner BFS).
  //    DXF handles are hex + CASE-INSENSITIVE; a producer may reference the
  //    same object as `XD1` in one place and `xd1` in another (Codex #251 F2).
  //    Key every map / set by `normalizeHandle` and normalize every lookup REF
  //    so case never breaks the join — exactly mirroring resolveMLeader /
  //    resolveAnnotativeContexts. The ORIGINAL handle strings are kept in the
  //    resolved payload (sourceEntityIds / *Handle fields) so round-trip
  //    fidelity is preserved.
  const assocByHandle = new Map<string, DxfAssocArrayObject>();
  const assocByOwner = new Map<string, DxfAssocArrayObject[]>();
  for (const a of assocArrays) {
    if (a.handle) assocByHandle.set(normalizeHandle(a.handle), a);
    if (a.ownerHandle) {
      const key = normalizeHandle(a.ownerHandle);
      const list = assocByOwner.get(key);
      if (list) list.push(a);
      else assocByOwner.set(key, [a]);
    }
  }

  // 1b. Index the named vertex action params (S-ARRAYRECT-PARAMS-DECODE).
  //     ACDBASSOCVERTEXACTIONPARAM records ("BaseNormal" / "Origin" /
  //     "basePoint") are NOT diverted out of rawObjectGroups (they round-trip
  //     verbatim through the raw passthrough); decode them here, keyed by
  //     NORMALIZED owner handle, so each array's subgraph walk can pick up the
  //     base-plane normal its action owns.
  const vertexParamsByOwner = new Map<string, AssocVertexActionParam[]>();
  for (const vp of collectAssocVertexActionParams(
    objectsSection.rawObjectGroups,
  )) {
    if (vp.ownerHandle === undefined) continue;
    const key = normalizeHandle(vp.ownerHandle);
    const list = vertexParamsByOwner.get(key);
    if (list) list.push(vp);
    else vertexParamsByOwner.set(key, [vp]);
  }

  // 2. Index the entity handles so a dependency FK can be checked against a
  //    real model entity in O(1). Keyed by normalized handle → ORIGINAL handle
  //    so a case-mismatched dependency FK still resolves AND the source id we
  //    record is the entity's canonical (model-id) handle string.
  const knownEntityHandles = new Map<string, string>();
  for (const e of entities) {
    if (e.common.handle) {
      knownEntityHandles.set(normalizeHandle(e.common.handle), e.common.handle);
    }
  }

  // 3. Index DICTIONARY entries by dict handle so we can follow an entity's
  //    extension dictionary to the assoc-network/action handles it references.
  const dictByHandle = buildDictionaryIndex(objectsSection);

  // 4. Per ARRAY entity: gather its assoc subgraph and resolve.
  for (const entity of entities) {
    // Only ARRAY block-reference entities carry an associative array. We do
    // not gate on a specific dxf-io entity type string here (the importer maps
    // the ARRAY block-ref to ARRAYRECT) — instead we attempt resolution for
    // every entity that has an extension dictionary reachable to an assoc
    // graph, and the bridge applies the result only to array entities.
    const entityHandle = entity.common.handle;
    const extDict = entity.common.extensionDictionaryHandle;
    if (!entityHandle || !extDict) continue;

    // Seed handles (NORMALIZED keys — see step 1). Everything the entity's
    // extension dictionary references that lands on an assoc object, plus any
    // assoc object owned (directly or via the dict chain) by that extension
    // dictionary.
    const seeds = new Set<string>();
    const dict =
      dictByHandle.get(extDict) ?? findDictByNormalizedHandle(dictByHandle, extDict);
    if (dict) {
      for (const ent of dict.entries) {
        const key = normalizeHandle(ent.handle);
        if (assocByHandle.has(key)) seeds.add(key);
      }
    }
    // Assoc objects whose owner is the ext-dict itself (AutoCAD often owns the
    // ACDBASSOCNETWORK directly under the entity's extension dictionary).
    for (const owned of assocByOwner.get(normalizeHandle(extDict)) ?? []) {
      if (owned.handle) seeds.add(normalizeHandle(owned.handle));
    }

    if (seeds.size === 0) continue;

    // BFS the owner graph from the seeds to collect the whole array subgraph.
    const subgraph = collectAssocSubgraph(seeds, assocByHandle, assocByOwner);
    if (subgraph.length === 0) continue;

    const resolved: ResolvedAssocArray = {
      entityHandle,
      associative: true,
      sourceEntityIds: [],
      dependencyHandles: [],
    };

    // Whether the subgraph is a RECTANGULAR-family array. Two source signals
    // (S-ARRAYRECT-PARAMS-DECODE): a standalone rect-params record (synthetic /
    // third-party shape) OR the action body's inline params class name (the
    // real AutoCAD shape — `1 AcDbAssocArrayRectangularParameters` after the
    // AcDbAssocArrayActionBody marker). Gates the named value-param mapping
    // below: polar/path "Items"/"ItemSpacing" carry different semantics and
    // must never be projected onto rectangular fields.
    let isRectFamily = false;
    // The inline per-item records of the FIRST plain action body (the same
    // record actionBodyHandle is taken from) — attached to the resolution
    // AFTER the loop, gated on the rectangular family
    // (S-ARRAYRECT-ITEMS-DECODE; see `ResolvedAssocArray.items`).
    let bodyItems: AssocArrayItem[] | undefined;

    for (const obj of subgraph) {
      if (obj.paramFamily === "rectangular" && obj.rectParams) {
        if (!resolved.rectParams) {
          resolved.rectParams = obj.rectParams;
          resolved.arrayParametersHandle = obj.handle;
        }
        isRectFamily = true;
      }
      if (
        obj.objectType.trim().toUpperCase() === "ACDBASSOCARRAYACTIONBODY" &&
        !resolved.actionBodyHandle
      ) {
        resolved.actionBodyHandle = obj.handle;
        bodyItems = obj.items;
      }
      // AW-1 — the owning ACDBASSOCACTION's own handle (round-trip linkage
      // for the write-back encoder). First match wins, mirroring the
      // actionBodyHandle capture above.
      if (
        obj.objectType.trim().toUpperCase() === "ACDBASSOCACTION" &&
        !resolved.actionHandle
      ) {
        resolved.actionHandle = obj.handle;
      }
      if (
        obj.objectType.trim().toUpperCase() === "ACDBASSOCARRAYACTIONBODY" &&
        obj.paramsClassName?.trim() === "AcDbAssocArrayRectangularParameters"
      ) {
        isRectFamily = true;
      }
      // Base-plane normal: the action-owned ACDBASSOCVERTEXACTIONPARAM named
      // "BaseNormal" (exact source spelling — ObjectARX setBasePlane / real
      // fixtures). First match wins; absent when the graph carries none.
      if (resolved.basePlaneNormal === undefined && obj.handle) {
        for (const vp of vertexParamsByOwner.get(
          normalizeHandle(obj.handle),
        ) ?? []) {
          if (vp.name === "BaseNormal") {
            resolved.basePlaneNormal = { ...vp.point };
            break;
          }
        }
      }
      if (obj.dependencyRefHandle !== undefined) {
        if (obj.handle) resolved.dependencyHandles.push(obj.handle);
        const ref = obj.dependencyRefHandle;
        // A dependency references the array entity itself OR a source entity.
        // The source entities are the ones that are NOT this ARRAY entity.
        // Compare + look up by NORMALIZED handle (case-insensitive hex) but
        // record the entity's CANONICAL (original) handle as the source id
        // (Codex #251 F2).
        const refKey = ref ? normalizeHandle(ref) : "";
        if (refKey && refKey !== normalizeHandle(entityHandle)) {
          const canonicalSource = knownEntityHandles.get(refKey);
          if (canonicalSource !== undefined) {
            if (!resolved.sourceEntityIds.includes(canonicalSource)) {
              resolved.sourceEntityIds.push(canonicalSource);
            }
          } else {
            result.orphanDependencyReferences += 1;
          }
        }
      }
    }

    // ── S-ARRAYRECT-PARAMS-DECODE: named value params (real AutoCAD shape) ──
    // Real AutoCAD stores the rectangular counts / spacings as NAMED value
    // params on the owning ACDBASSOCACTION — not as a standalone params
    // record. Merge them into the resolved rectParams: a standalone record's
    // positional decode wins per field (it is the more explicit source); the
    // named params fill the fields it did not provide. Gated on the
    // rectangular family so polar/path "Items"/"ItemSpacing" (different
    // semantics) are never mapped. The parse-side objects are never mutated —
    // the merge builds a fresh snapshot (mirrors the annotative resolver's
    // clone discipline).
    if (isRectFamily) {
      const mergeResult = mergeNamedRectValueParams(subgraph, resolved.rectParams);
      if (mergeResult !== undefined) {
        resolved.rectParams = mergeResult.rectParams;
        // @codex P2 (local review, round-3) — bind `actionHandle` to the
        // ACTION THAT ACTUALLY SUPPLIED the merged named value params, not
        // just "the first ACDBASSOCACTION anywhere in the subgraph" (the
        // fallback capture a few lines above). A network with more than one
        // action (e.g. an unrelated action reached earlier in BFS order)
        // would otherwise bind write-back to an action record with NO
        // "Items"/"Rows"/… value params at all — `patchAssocArrayGraphRawGroups`
        // reports every named-form field "missing" and edits silently fail
        // to persist. Overwrites the earlier fallback capture whenever the
        // merge actually resolved a source action (the correct, specific
        // answer beats the "first found" guess).
        if (mergeResult.actionHandle !== undefined) {
          resolved.actionHandle = mergeResult.actionHandle;
        }
        // @codex P2 (local review, round-4) — record whether axisAngle
        // SPECIFICALLY came from the named form (genuinely degrees) so
        // consumers never have to re-derive "which form supplied it" from
        // the ambiguous fact "some axisAngle value is present."
        if (mergeResult.axisAngleFromNamedForm) {
          resolved.axisAngleFromNamedForm = true;
        }
        // @codex P2 ("Track the owning action per named field") — carry the
        // per-field split-ownership map through when the merge detected a
        // genuine multi-action split (absent for the common single-action
        // case — see `mergeNamedRectValueParams`'s collapse-to-undefined
        // rule).
        if (mergeResult.ownerHandleByField !== undefined) {
          resolved.ownerHandleByField = mergeResult.ownerHandleByField;
        }
      }
    }

    // ── S-ARRAYRECT-ITEMS-DECODE: per-item records (rect family only) ──
    // Same family gate as the value-param mapping above: the item grammar is
    // family-shared (ODA base-class filer) but the only model home today is
    // ArrayRectEntity.assocItems — a polar/path graph keeps its items
    // raw-only (verbatim via rawGroups) until those model entities exist.
    if (isRectFamily && bodyItems !== undefined && bodyItems.length > 0) {
      resolved.items = bodyItems;
    }

    result.byEntityHandle.set(entityHandle, resolved);
  }

  return result;
}

/**
 * A named-value-param → rectangular-field mapping entry. Exported (AW-1,
 * docs/MASTERPLAN.md AW.2 — "экспорт RECT_NAMED_VALUE_PARAMS-таблицы как
 * одной истины read/write") so the write-back encoder
 * (`assocArrayPatch.ts`) locates the SAME name/field/code triples the
 * import-side merge below uses — one table drives both directions, they can
 * never drift apart.
 */
export type RectNamedValueParamEntry = {
  name: string;
  field: keyof Pick<
    AssocArrayRectParams,
    | "columnCount"
    | "rowCount"
    | "levelCount"
    | "columnSpacing"
    | "rowSpacing"
    | "levelSpacing"
    | "rowElevation"
    | "axisAngle"
  >;
  /** Expected EvalVariant group code: 90 (int32) for counts, 40 (real) else. */
  code: number;
};

/**
 * Per-field owning-ACDBASSOCACTION-handle map (@codex P2 "Track the owning
 * action per named field") — the write-back encoder (`assocArrayPatch.ts`)
 * uses this to patch a SPLIT-ownership array's named value params into
 * MULTIPLE action records instead of assuming every field lives on the one
 * `actionHandle`. Exported so the type is shared, unmodified, across
 * `mergeNamedRectValueParams` (produces it), `ResolvedAssocArray` /
 * `AssocArrayHandles` (carry it through the model), and
 * `AssocArrayGraphPatchTarget` (consumes it) — one shape, never re-declared.
 */
export type AssocArrayFieldOwnerMap = Partial<
  Record<RectNamedValueParamEntry["field"], string>
>;

/**
 * Named value-param → rectangular-field mapping (audit §12
 * S-ARRAYRECT-PARAMS-DECODE). Source of truth for the names + value kinds:
 * ODA `OdDbImpAssocArrayRectangularParameters` / `...CommonParameters`
 * (Items = column count, ItemSpacing = column spacing, AxesAngle in RADIANS)
 * byte-matched against two real AutoCAD fixtures. Counts are int-coded
 * (group 90), spacings / angle / elevation real-coded (group 40) — a value
 * whose group code does not match its documented kind is NOT projected
 * (preserve-source-never-infer; it still round-trips verbatim).
 */
export const RECT_NAMED_VALUE_PARAMS: readonly RectNamedValueParamEntry[] = [
  { name: "Items", field: "columnCount", code: 90 },
  { name: "Rows", field: "rowCount", code: 90 },
  { name: "Levels", field: "levelCount", code: 90 },
  { name: "ItemSpacing", field: "columnSpacing", code: 40 },
  { name: "RowSpacing", field: "rowSpacing", code: 40 },
  { name: "LevelSpacing", field: "levelSpacing", code: 40 },
  { name: "RowElevation", field: "rowElevation", code: 40 },
  { name: "AxesAngle", field: "axisAngle", code: 40 },
];

/**
 * AW-1 units seam (docs/MASTERPLAN.md AW.1 п.122 — "Латентный units-seam,
 * чинить в AW-1"). AutoCAD's "AxesAngle" named value param persists RADIANS
 * (ODA `OdDbImpAssocArrayCommonParameters`); the model's `axisAngle` field
 * convention is DEGREES — matches the DXF group-50 INSERT rotation
 * convention the non-associative MInsert-grid emit path already uses
 * (`dxfBridge.arrayMinsert.ts` writes `entity.axisAngle` straight to group
 * 50, which is degrees per the Autodesk DXF reference). Exported so the
 * write-back encoder (`assocArrayPatch.ts`) applies the EXACT inverse when
 * patching the named param back — one pair, both directions, never drifts.
 * Deliberately NOT applied to the standalone `ACDBASSOCARRAYRECTANGULARPARAMETERS`
 * positional form: that form is a synthetic/ODA-sample shape real AutoCAD
 * never emits (no fixture proves its axisAngle unit convention), and the
 * existing AW-0 byte-stability matrix already treats it as a pass-through
 * numeric (preserve-source-never-infer — do not invent a conversion with no
 * evidence).
 */
export const axesAngleRadiansToModelDegrees = (rad: number): number =>
  (rad * 180) / Math.PI;
export const axesAngleModelDegreesToRadians = (deg: number): number =>
  (deg * Math.PI) / 180;

/**
 * Merge the named value params carried by the subgraph's ACDBASSOCACTION
 * record(s) into a FRESH rectParams snapshot. Per-field precedence: an
 * existing (standalone-record) value wins; a named param fills the gap only.
 * Returns undefined when NEITHER a value was merged NOR an owning action was
 * identified at all (absence-preserving — the caller keeps whatever it had,
 * including undefined).
 *
 * Also returns `actionHandle` — the handle of the ACDBASSOCACTION record that
 * GENUINELY owns the array's named value params (@codex P2, local review
 * round-3; widened by @codex P2 "Bind the action that owns named params") —
 * so the caller can bind write-back's `resolved.actionHandle` to that action
 * instead of whichever ACDBASSOCACTION a separate "first found in the
 * subgraph" scan happened to reach first. This is identified from the FIRST
 * `RECT_NAMED_VALUE_PARAMS` entry (table order) whose name resolves to a
 * genuine single-literal value param on SOME action in the subgraph —
 * REGARDLESS of whether that field's VALUE actually gets merged (a standalone
 * positional record can win every single field's value while the named form
 * still genuinely exists and must remain the write-back target for the named
 * emission side). Absent when NO action in the subgraph owns any
 * RECT_NAMED_VALUE_PARAMS-shaped param at all (a malformed graph, or a
 * network whose actions are all foreign to this array). In the dominant
 * real-world case (one action per array) `actionHandle` and "first found in
 * the subgraph" trivially agree; a multi-action network — including one where
 * a standalone record supplies every field's value — is where they can
 * diverge.
 *
 * Also returns `axisAngleFromNamedForm` (@codex P2, local review round-4) —
 * true iff `axisAngle` specifically was filled by THIS merge (i.e. the
 * named-form units-seam conversion applied to it), false when the standalone
 * positional record already supplied it (`existing.axisAngle !== undefined`
 * on entry — "standalone wins" skips it here) or when no axisAngle resolved
 * at all. See {@link ResolvedAssocArray.axisAngleFromNamedForm}'s doc for why
 * this distinction matters.
 *
 * Also returns `ownerHandleByField` (@codex P2 "Track the owning action per
 * named field") — the SAME per-field ownership `byName` already tracks by
 * NAME, re-keyed by FIELD, for EVERY field that resolved to a genuine
 * single-literal RECT_NAMED_VALUE_PARAMS match — REGARDLESS of whether the
 * standalone record won that field's VALUE (mirrors `actionHandle`'s own
 * "genuine match" gate above; a field can be real AND still lose its value to
 * a standalone record while still needing its OWN action targeted on
 * write-back). `actionHandle` alone is only a safe write-back target when
 * every field the array actually persists (via the named form) lives on ONE
 * action — the overwhelming common real-world shape. A network whose named
 * params are genuinely SPLIT across more than one ACDBASSOCACTION (rare, but
 * `byName` already has the per-name ownership data to detect it) needs THIS
 * per-field map so write-back can patch each field into the record that
 * actually carries it, instead of blindly targeting whichever action
 * `actionHandle` happened to bind to first. `undefined` when every genuinely
 * matched field's owner is the SAME single handle (the common case — the
 * caller can fall back to `actionHandle` uniformly and never has to special-
 * case a length-1-map).
 */
function mergeNamedRectValueParams(
  subgraph: ReadonlyArray<DxfAssocArrayObject>,
  existing: AssocArrayRectParams | undefined,
):
  | {
      rectParams: AssocArrayRectParams;
      actionHandle: string | undefined;
      axisAngleFromNamedForm: boolean;
      ownerHandleByField: AssocArrayFieldOwnerMap | undefined;
    }
  | undefined {
  // Collect the single-value numeric params from every action in the
  // subgraph (real graphs have exactly one action; first name wins) —
  // tracking each param's OWNING action handle alongside it.
  const byName = new Map<
    string,
    { vp: AssocActionValueParam; ownerHandle: string | undefined }
  >();
  for (const obj of subgraph) {
    if (obj.objectType.trim().toUpperCase() !== "ACDBASSOCACTION") continue;
    for (const vp of obj.valueParams ?? []) {
      if (!byName.has(vp.name)) byName.set(vp.name, { vp, ownerHandle: obj.handle });
    }
  }
  if (byName.size === 0) return undefined;

  // Fresh snapshot — never mutate the parse-side rectParams object (it is the
  // round-trip copy on objectsSection.assocArrays[]). The positional arrays
  // are carried over by reference but never mutated downstream.
  const merged: AssocArrayRectParams = existing
    ? { ...existing }
    : { intParams: [], doubleParams: [], expressions: [] };

  let filledAny = false;
  let actionHandle: string | undefined;
  let axisAngleFromNamedForm = false;
  let ownerHandleByField: AssocArrayFieldOwnerMap | undefined;
  for (const { name, field, code } of RECT_NAMED_VALUE_PARAMS) {
    const entry = byName.get(name);
    if (entry === undefined) continue;
    const { vp, ownerHandle } = entry;
    // Single literal value with the documented group code only — a formula
    // chain (multiple EvalVariants) or an unexpected value kind stays
    // raw-only rather than being guessed at. Below this point the param is a
    // GENUINE, well-formed RECT_NAMED_VALUE_PARAMS match on `ownerHandle`.
    if (vp.values.length !== 1) continue;
    const v = vp.values[0]!;
    if (v.code !== code) continue;
    // @codex P2 ("Bind the action that owns named params") — capture the
    // owning action's handle on the FIRST genuine match, BEFORE the
    // standalone-wins check below. A field can be a real, well-formed named
    // value param AND still lose its VALUE to an existing standalone record —
    // that does not make the action any less the true owner of the graph's
    // named form, and write-back still needs to target it for every OTHER
    // field the standalone record does not carry.
    if (actionHandle === undefined) actionHandle = ownerHandle;
    // @codex P2 ("Track the owning action per named field") — record THIS
    // field's OWN owner regardless of which action `actionHandle` bound to.
    // A single-action graph fills every entry with the SAME handle (the
    // "undefined when no genuine split" collapse below discards it); a
    // split-ownership graph is exactly where this per-field map disagrees
    // with `actionHandle` for some fields — the case `actionHandle` alone
    // cannot represent.
    if (ownerHandle !== undefined) {
      (ownerHandleByField ??= {})[field] = ownerHandle;
    }
    if (merged[field] !== undefined) continue; // standalone record wins the VALUE
    // AW-1 units seam — see `axesAngleRadiansToModelDegrees` doc: the named
    // "AxesAngle" param is radians on the wire, degrees on the model.
    if (name === "AxesAngle") {
      merged[field] = axesAngleRadiansToModelDegrees(v.value);
      axisAngleFromNamedForm = true;
    } else {
      merged[field] = v.value;
    }
    filledAny = true;
  }
  // Collapse to `undefined` when every genuinely matched field resolved to
  // the SAME single handle (the overwhelming common case — one action per
  // array) — the caller falls back to the plain `actionHandle` uniformly and
  // this map only ever carries data for the rare genuine multi-action split.
  let finalOwnerHandleByField: AssocArrayFieldOwnerMap | undefined;
  if (ownerHandleByField !== undefined) {
    const distinctHandles = new Set(Object.values(ownerHandleByField));
    if (distinctHandles.size > 1) finalOwnerHandleByField = ownerHandleByField;
  }
  // @codex P2 ("Bind the action that owns named params") — return a result
  // whenever EITHER a value was merged OR an owning action was identified
  // (the "positional record already supplied every matching field" case,
  // finding's exact gap): the caller (`resolveAssocArrays`) only reads
  // `rectParams`/`axisAngleFromNamedForm` when they actually changed
  // anything, but always wants the CORRECT `actionHandle` when one exists.
  return filledAny || actionHandle !== undefined
    ? {
        rectParams: merged,
        actionHandle,
        axisAngleFromNamedForm,
        ownerHandleByField: finalOwnerHandleByField,
      }
    : undefined;
}

/**
 * Look up a DICTIONARY node by a possibly case-mismatched handle. The shared
 * `buildDictionaryIndex` keys by the raw handle (to preserve the annotative
 * resolver's behaviour); this fallback re-scans by NORMALIZED handle so an
 * entity's extension-dictionary 360 that differs in case from the DICTIONARY
 * record's group 5 still resolves (Codex #251 F2). Returns undefined when no
 * dict matches.
 */
function findDictByNormalizedHandle(
  dictByHandle: ReadonlyMap<string, DictNode>,
  handle: string,
): DictNode | undefined {
  const key = normalizeHandle(handle);
  for (const [h, node] of dictByHandle) {
    if (normalizeHandle(h) === key) return node;
  }
  return undefined;
}

/**
 * BFS the AcDbAssoc* owner graph starting from `seeds`, returning every assoc
 * object in the array network — BOTH directions of the owner edge:
 *
 *   - DOWN (owner → children): an object's children are the assoc objects whose
 *     ownerHandle is that object's handle.
 *   - UP (child → owner): an object's owner is the assoc object named by its
 *     ownerHandle.
 *
 * Walking BOTH directions matters because the entity's extension-dictionary 360
 * may point at the ACDBASSOCARRAYACTIONBODY (not the network). Seeding there and
 * only walking DOWN reaches the params the body owns but NEVER walks back to the
 * parent ACDBASSOCACTION — so the dependency records owned by that action are
 * omitted and `sourceEntityIds` comes back empty (Codex #251 F4). Climbing the
 * owner chain back to the action/network and then re-descending collects the
 * full network → action → body → params + dependencies subgraph regardless of
 * which node the ext-dict points at. The shared `visited` set is the cycle
 * guard; the iteration bound caps degenerate malformed graphs.
 */
function collectAssocSubgraph(
  seeds: ReadonlySet<string>,
  assocByHandle: ReadonlyMap<string, DxfAssocArrayObject>,
  assocByOwner: ReadonlyMap<string, DxfAssocArrayObject[]>,
): DxfAssocArrayObject[] {
  const visited = new Set<string>();
  const out: DxfAssocArrayObject[] = [];
  const queue: string[] = [...seeds];
  let guard = 0;
  while (queue.length > 0 && guard < 10000) {
    guard += 1;
    const handle = queue.shift()!;
    if (visited.has(handle)) continue;
    visited.add(handle);
    const obj = assocByHandle.get(handle);
    if (obj) out.push(obj);
    // DOWN — enqueue children: assoc objects owned by this handle. Keys are
    // normalized (Codex #251 F2) so enqueue the normalized child handle.
    for (const child of assocByOwner.get(handle) ?? []) {
      if (!child.handle) continue;
      const childKey = normalizeHandle(child.handle);
      if (!visited.has(childKey)) queue.push(childKey);
    }
    // UP — enqueue this object's owner so we walk back to the parent
    // action/network and re-descend onto its other children (e.g. the
    // dependency records). Without this, an ext-dict 360 pointing at the
    // action body would never reach the action's dependencies (Codex #251 F4).
    if (obj?.ownerHandle) {
      const ownerKey = normalizeHandle(obj.ownerHandle);
      if (!visited.has(ownerKey)) queue.push(ownerKey);
    }
  }
  return out;
}

// ── Viewport / XCLIP spatial-filter resolution (EPIC-VIEWPORT-CLIP SLICE 3) ──

/**
 * DICTIONARY entry name that an entity's extension dictionary uses to point at
 * the FILTER dictionary. Exact AutoCAD identifier — ezdxf `xclip.ACAD_FILTER`
 * (`= "ACAD_FILTER"`).
 */
const ACAD_FILTER_DICT_ENTRY = "ACAD_FILTER";
/**
 * DICTIONARY entry name that the ACAD_FILTER dictionary uses to point at the
 * SPATIAL_FILTER object. Exact AutoCAD identifier — ezdxf `xclip.SPATIAL`
 * (`= "SPATIAL"`).
 */
const SPATIAL_FILTER_DICT_ENTRY = "SPATIAL";

/** A resolved clip-polygon vertex in WCS (the 2D clip plane carries z=0). */
export type ViewportClipPoint = { x: number; y: number; z: number };

/**
 * Result of `resolveViewportClip`. Mirrors `AnnotativeContextResolution` /
 * `AssocArrayResolution`: a per-viewport map keyed by VIEWPORT handle plus
 * orphan telemetry for the audit log. The clip path is the WCS clip polygon
 * the renderer stencils against.
 */
export type ViewportClipResolution = {
  /**
   * Map of VIEWPORT entity handle → resolved WCS clip polygon. A viewport
   * whose ext-dict chain dangles (no ACAD_FILTER dict / no SPATIAL entry / no
   * matrix pair) is simply ABSENT from the map — no fabricated clip.
   */
  clipPathByViewportHandle: Map<string, ViewportClipPoint[]>;
  /**
   * Map of VIEWPORT entity handle → the SOURCE-preserved clip boundary KIND
   * (`"rectangular"` for a 2-point SPATIAL_FILTER boundary, `"polygonal"` for
   * a >2-point one). Present for exactly the handles in
   * `clipPathByViewportHandle`. The resolved `clipPath` cannot be used to
   * re-derive this downstream — a rectangular 2-point boundary is expanded into
   * a 4-corner polygon, so `clipPath.length` is ≥3 for both kinds — so the
   * inspector reads THIS field to report the true clip mode for a polygon
   * SPATIAL_FILTER whose group-90 non-rect bit is 0 (@codex #496 P3).
   */
  clipBoundaryKindByViewportHandle: Map<string, "rectangular" | "polygonal">;
  /**
   * Count of viewports whose extension-dictionary clip chain dangled (an
   * ext-dict that did not resolve a SPATIAL_FILTER, or a SPATIAL_FILTER whose
   * boundary / matrix was unusable). Surfaced for audit-log telemetry exactly
   * like `orphanScaleReferences` — never throws; the source SPATIAL_FILTER
   * records still round-trip verbatim through `objectsSection.spatialFilters`.
   */
  orphanViewportClipReferences: number;
};

/**
 * Apply a SPATIAL_FILTER `inverseInsertMatrix` (4×3 column-major, 12 reals)
 * to a boundary point, mirroring ezdxf's `Matrix44.transform`.
 *
 * The 12 reals `v` are stored COLUMN-MAJOR (DXF group 40 ×12: column 0 =
 * v[0..3], column 1 = v[4..7], column 2 = v[8..11]) — confirmed against
 * ezdxf `entities/spatial_filter.py::to_matrix`, which loads them into a
 * row-major `Matrix44` as `[v0,v4,v8,0, v1,v5,v9,0, v2,v6,v10,0, v3,v7,v11,1]`.
 * ezdxf `Matrix44.transform` applies ROW-VECTOR × MATRIX so the output is:
 *
 *   out.x = v0·x + v1·y + v2·z + v3
 *   out.y = v4·x + v5·y + v6·z + v7
 *   out.z = v8·x + v9·y + v10·z + v11
 *
 * i.e. the three rows of the affine are [v0..v3], [v4..v7], [v8..v11]. This
 * matches the SLICE-2 test fixtures (identity = [1,0,0,0, 0,1,0,0, 0,0,1,0]).
 *
 * For an XCLIP'd block reference ezdxf computes the WCS path in TWO steps
 * (`xclip.py`): (1) `inverse_insert_matrix.transform(boundary)` → block
 * coordinates, then (2) `insert.matrix44().transform(...)` → WCS. A paper-
 * space VIEWPORT (VPCLIP) is NOT a block insert — its clip boundary already
 * lives in the paper-space plane and its owning transform is identity, so
 * step (1) alone yields the WCS (paper-space) clip polygon. The forward
 * `transformMatrix` and the insert second step are preserved verbatim on the
 * typed record for the block-XCLIP case (deferred), never consumed here.
 */
function transformByInverseInsertMatrix(
  v: ReadonlyArray<number>,
  pt: { x: number; y: number; z: number },
): ViewportClipPoint {
  return {
    x: v[0]! * pt.x + v[1]! * pt.y + v[2]! * pt.z + v[3]!,
    y: v[4]! * pt.x + v[5]! * pt.y + v[6]! * pt.z + v[7]!,
    z: v[8]! * pt.x + v[9]! * pt.y + v[10]! * pt.z + v[11]!,
  };
}

/**
 * Expand a SPATIAL_FILTER's boundary points into the renderable clip polygon.
 *
 * Per the SPATIAL_FILTER spec (ezdxf `spatial_filter.py` + Autodesk DXF
 * reference): a TWO-point boundary is a RECTANGULAR clip — the two points are
 * the lower-left / upper-right corners and the renderer fills the 4-corner
 * rectangle; a boundary with MORE than two points is an explicit polygon.
 * Each boundary point is the (x,y) ordinate from DXF 10/20 with z=0 (the clip
 * plane). The points are then transformed to WCS by the inverse-insert matrix.
 *
 * Returns null when the boundary is unusable (fewer than two points, or no
 * 12-real inverse matrix to transform with) so the caller counts an orphan.
 */
function expandSpatialFilterClipPolygon(
  filter: DxfSpatialFilter,
): { path: ViewportClipPoint[]; kind: "rectangular" | "polygonal" } | null {
  const pts = filter.boundaryPoints;
  if (!pts || pts.length < 2) return null;
  // A degenerate record without the full matrix pair carries an empty
  // inverseInsertMatrix (parser emits [] — see parseSpatialFilterEntry). With
  // no transform the boundary cannot be mapped to WCS faithfully; treat it as
  // an orphan rather than emitting raw (untransformed) ordinates as if WCS.
  const m = filter.inverseInsertMatrix;
  if (!m || m.length !== 12) return null;

  // The SOURCE-preserved clip KIND: 2 boundary points ⇒ a RECTANGULAR clip
  // (the two opposite corners are expanded into the 4-corner rect below); >2 ⇒
  // an explicit POLYGON. This is the ONE place the original point count is
  // known — after expansion the returned `path.length` is ≥3 for BOTH cases (a
  // rectangle becomes 4 corners), so a downstream consumer CANNOT re-infer
  // "rectangular vs polygonal" from the resolved polygon. Capturing it here
  // lets the viewport inspector report the true clip mode for an imported
  // polygon SPATIAL_FILTER whose group-90 non-rect bit is 0 (@codex #496 P3).
  const kind = pts.length === 2 ? "rectangular" : "polygonal";

  // Build the local-plane polygon (z=0). 2 points ⇒ rectangle corners; >2 ⇒
  // explicit polygon vertices.
  let local: Array<{ x: number; y: number; z: number }>;
  if (pts.length === 2) {
    const [a, b] = pts as [{ x: number; y: number }, { x: number; y: number }];
    const minX = Math.min(a.x, b.x);
    const maxX = Math.max(a.x, b.x);
    const minY = Math.min(a.y, b.y);
    const maxY = Math.max(a.y, b.y);
    local = [
      { x: minX, y: minY, z: 0 },
      { x: maxX, y: minY, z: 0 },
      { x: maxX, y: maxY, z: 0 },
      { x: minX, y: maxY, z: 0 },
    ];
  } else {
    local = pts.map((p) => ({ x: p.x, y: p.y, z: 0 }));
  }

  return { path: local.map((p) => transformByInverseInsertMatrix(m, p)), kind };
}

/**
 * Second-pass resolver for paper-space VIEWPORT clip boundaries
 * (EPIC-VIEWPORT-CLIP SLICE 3). Clones the shape of
 * `resolveAnnotativeContexts` / `resolveAssocArrays`: every collection is
 * hashed once into Maps, then a per-viewport walk joins the VIEWPORT to its
 * SPATIAL_FILTER and produces the WCS clip polygon.
 *
 * Resolution chain (verified against the ezdxf `xclip.py::get_spatial_filter`
 * reference impl + Autodesk DXF reference SPATIAL_FILTER):
 *
 *   VIEWPORT entity — `common.extensionDictionaryHandle`
 *     ↓
 *   DICTIONARY — the entity's extension dictionary
 *     ↓ (entry name "ACAD_FILTER" → handle)
 *   DICTIONARY — the filter dictionary
 *     ↓ (entry name "SPATIAL" → handle)
 *   SPATIAL_FILTER record — `objectsSection.spatialFilters[]`
 *     ↓ boundaryPoints (OCS) × inverseInsertMatrix → WCS clip polygon
 *
 * Robustness fallback (mirrors how the annotative resolver tries a DIRECT
 * anchor before the dict chain): if the ext-dict has no "ACAD_FILTER" sub-dict
 * but a SPATIAL_FILTER's `ownerHandle` points DIRECTLY at the viewport's
 * extension dictionary, that filter is used. Handle lookups are
 * case-insensitive hex (`normalizeHandle`), matching the other resolvers.
 *
 * Falls back gracefully: a viewport with no ext-dict, no reachable
 * SPATIAL_FILTER, or an unusable boundary/matrix is simply absent from the
 * result map (and counted as an orphan). Nothing throws; the SPATIAL_FILTER
 * records round-trip verbatim through `objectsSection.spatialFilters`.
 */
export function resolveViewportClip(
  objectsSection: DxfObjectsSection,
  viewports: ReadonlyArray<DxfSupportedEntity>,
): ViewportClipResolution {
  const result: ViewportClipResolution = {
    clipPathByViewportHandle: new Map(),
    clipBoundaryKindByViewportHandle: new Map(),
    orphanViewportClipReferences: 0,
  };

  const filters = objectsSection.spatialFilters ?? [];
  // NOTE: do NOT early-return when there are zero SPATIAL_FILTER records (Codex
  // #326 r4 #2). A malformed import can carry a viewport whose ext-dict →
  // ACAD_FILTER → "SPATIAL" chain resolves to a handle with NO SPATIAL_FILTER
  // target (a dangling clip chain) while the OBJECTS section holds no spatial
  // filters at all. Skipping the dictionary walk here would leave
  // `orphanViewportClipReferences` at 0 and report the broken import as CLEAN,
  // hiding the dangling viewport-clip reference from importDiagnostics. The walk
  // below is cheap when no chain resolves (no dict ⇒ no acadFilterHandle ⇒ no
  // orphan), so always run it. (A producer that emits filters takes the resolve
  // path; one that emits a dangling chain with no filters takes the orphan path.)

  // 1. Index SPATIAL_FILTER records BOTH by their own handle (group 5) and by
  //    their owner handle (group 330 — the ext-dict / filter-dict that owns
  //    them) so the direct-owner fallback is O(1). Keys normalized (Codex
  //    #251 F2 case-insensitive hex); original handle strings kept on payload.
  const filterByHandle = new Map<string, DxfSpatialFilter>();
  const filterByOwner = new Map<string, DxfSpatialFilter[]>();
  for (const f of filters) {
    if (f.handle) filterByHandle.set(normalizeHandle(f.handle), f);
    if (f.ownerHandle) {
      const key = normalizeHandle(f.ownerHandle);
      const list = filterByOwner.get(key);
      if (list) list.push(f);
      else filterByOwner.set(key, [f]);
    }
  }

  // 2. Index DICTIONARY records by handle — the shared one-truth index used by
  //    every owner-chain-walking resolver.
  const dictByHandle = buildDictionaryIndex(objectsSection);

  // Resolve a dictionary entry's handle by entry name (case-SENSITIVE — DXF
  // dictionary entry names like "ACAD_FILTER"/"SPATIAL" are exact AutoCAD
  // identifiers, NOT handles), reading from a DictNode's entries[].
  const dictEntryHandle = (
    dict: DictNode | undefined,
    name: string,
  ): string | undefined => {
    if (!dict) return undefined;
    for (const e of dict.entries) {
      if (e.name === name) return e.handle;
    }
    return undefined;
  };

  // 3. Per VIEWPORT entity: walk the ext-dict chain to its SPATIAL_FILTER and
  //    project the WCS clip polygon.
  for (const vp of viewports) {
    if (vp.type !== "VIEWPORT") continue;
    const vpHandle = vp.common.handle;
    const extDictHandle = vp.common.extensionDictionaryHandle;
    if (!vpHandle || !extDictHandle) continue;

    // ext-dict → "ACAD_FILTER" dict → "SPATIAL" → SPATIAL_FILTER (ezdxf chain).
    let filter: DxfSpatialFilter | undefined;
    const extDict =
      dictByHandle.get(extDictHandle) ??
      findDictByNormalizedHandle(dictByHandle, extDictHandle);
    const acadFilterHandle = dictEntryHandle(extDict, ACAD_FILTER_DICT_ENTRY);
    if (acadFilterHandle) {
      const filterDict =
        dictByHandle.get(acadFilterHandle) ??
        findDictByNormalizedHandle(dictByHandle, acadFilterHandle);
      const spatialHandle = dictEntryHandle(
        filterDict,
        SPATIAL_FILTER_DICT_ENTRY,
      );
      if (spatialHandle) {
        filter = filterByHandle.get(normalizeHandle(spatialHandle));
      }
    }

    // Fallback: a SPATIAL_FILTER owned DIRECTLY by the viewport's extension
    // dictionary (some producers skip the intermediate ACAD_FILTER sub-dict).
    if (!filter) {
      const owned = filterByOwner.get(normalizeHandle(extDictHandle));
      if (owned && owned.length > 0) filter = owned[0];
    }

    if (!filter) {
      // The viewport carries an ext-dict but the clip chain did not reach a
      // SPATIAL_FILTER — count it only when the ext-dict actually resolved to a
      // dictionary (a dangling chain). A viewport with an unrelated ext-dict
      // (e.g. only an annotation-scale XRECORD, no clip) is NOT an orphan.
      if (acadFilterHandle !== undefined) {
        result.orphanViewportClipReferences += 1;
      }
      continue;
    }

    // A SPATIAL_FILTER whose DXF group 71 (clipActive / ezdxf
    // `is_clipping_enabled`) is 0 is an INACTIVE clip: the filter is present and
    // round-trips verbatim, but clipping is turned OFF, so AutoCAD renders the
    // viewport UNCLIPPED (full rectangular window). Skip it WITHOUT producing a
    // clipPath and WITHOUT counting an orphan — the reference resolved fine, the
    // clip is simply disabled (not a dangling/broken chain). (Codex #326 P2.)
    if (!filter.clipActive) {
      continue;
    }

    const clip = expandSpatialFilterClipPolygon(filter);
    if (!clip) {
      result.orphanViewportClipReferences += 1;
      continue;
    }
    result.clipPathByViewportHandle.set(vpHandle, clip.path);
    result.clipBoundaryKindByViewportHandle.set(vpHandle, clip.kind);
  }

  return result;
}
